/**
 * Intercom Service
 * Centralized service for tracking user attributes and events to Intercom.
 * Works alongside IntercomContext for React components and can be used in services.
 */

import { FormQuestion, FormResponse } from "../types/form";

// Question text to Intercom attribute mapping based on questionnaire
// Mapping based on exact Intercom attribute names (case-sensitive!)
const QUESTION_TO_ATTRIBUTE_MAP: Record<string, string> = {
  "please provide your full name": "Name",
  "please provide your email": "Email",
  "what type of investor are you": "Investor Stype",
  "do you have an interactive brokers (ibkr) account": "IBKR Enabled",
  "what is the size of your portfolio": "Portfolio amount to be managed",
  "what provider are you most interested in on plutus": "Signup Persona",
  "if given access will you start investing on plutus": "Customer Intent to Invest",
  "how did you hear about plutus": "Customer Referrer",
  "is there anything else that you would like us to know?": "Onboarding - Open Comments",
};

// Event names for Intercom tracking
export const IntercomEvents = {
  QUESTIONNAIRE_STARTED: "questionnaire_started",
  QUESTIONNAIRE_COMPLETED: "questionnaire_completed",
  QUESTIONNAIRE_ANSWER_SUBMITTED: "questionnaire_answer_submitted",
  PORTFOLIO_VIEWED: "portfolio_viewed",
  BASKET_VIEWED: "basket_viewed",
  DISCOVERY_PAGE_VIEWED: "discovery_page_viewed",
  LOGIN_SUCCESS: "login_success",
  SESSION_STARTED: "session_started",
  // Signup funnel events
  SIGNUP_STARTED: "signup_started",
  SIGNUP_EMAIL_SUBMITTED: "signup_email_submitted",
  SIGNUP_COMPLETED: "signup_completed",
  // Login funnel events
  LOGIN_STARTED: "login_started",
} as const;

export type IntercomEventName = (typeof IntercomEvents)[keyof typeof IntercomEvents];

// Custom attributes interface for type safety
// Attribute names must match exact Intercom attribute names (case-sensitive!)
export interface IntercomCustomAttributes {
  // Questionnaire attributes (exact Intercom attribute names)
  "Investor Stype"?: string;
  "IBKR Enabled"?: string;
  "Portfolio amount to be managed"?: string;
  "Signup Persona"?: string;
  "Customer Intent to Invest"?: string;
  "Customer Referrer"?: string;
  "Onboarding - Open Comments"?: string;

  // Questionnaire progress
  questionnaire_completed?: boolean;
  questionnaire_completion_date?: string;
  questionnaire_progress_percent?: number;

  // Engagement metrics
  total_sessions?: number;
  last_page_viewed?: string;
  portfolios_viewed_count?: number;
  baskets_viewed_count?: number;
  last_active_date?: string;

  // Allow additional custom attributes
  [key: string]: string | number | boolean | undefined;
}

// Event metadata interfaces - include index signature for Record<string, unknown> compatibility
export interface QuestionnaireEventMetadata {
  category?: string;
  questionCount?: number;
  questionId?: number;
  questionText?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface PortfolioViewEventMetadata {
  portfolioId: number;
  portfolioName?: string;
  providerName?: string;
  providerId?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface BasketViewEventMetadata {
  basketId: number;
  basketName?: string;
  providerName?: string;
  providerId?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface DiscoveryEventMetadata {
  filterUsed?: boolean;
  resultsCount?: number;
  typeFilter?: string;
  [key: string]: string | number | boolean | undefined;
}

// Type for the Intercom update function (injected from context)
type IntercomUpdateFn = (data: { customAttributes?: Record<string, string | number | boolean | undefined> }) => void;

// Type for the Intercom trackEvent function
type IntercomTrackEventFn = (eventName: string, metadata?: Record<string, unknown>) => void;

/**
 * Intercom Service Class
 * Provides methods for updating user attributes and tracking events.
 * The update and trackEvent functions are injected from IntercomContext.
 */
class IntercomService {
  private updateFn: IntercomUpdateFn | null = null;
  private trackEventFn: IntercomTrackEventFn | null = null;
  private isInitialized = false;
  private canEnrichActor = () => true;

  /**
   * Initialize the service with Intercom functions from context.
   * Called by IntercomContext when it mounts.
   */
  initialize(updateFn: IntercomUpdateFn, trackEventFn: IntercomTrackEventFn, canEnrichActor = () => true): void {
    this.canEnrichActor = canEnrichActor;
    this.updateFn = updateFn;
    this.trackEventFn = trackEventFn;
    this.isInitialized = true;
  }

  /**
   * Check if service is ready to use
   */
  /** Fence optional enrichment started before an asynchronous form request. */
  captureSession(): () => boolean {
    const updateFn = this.updateFn;
    return () => this.isReady() && this.updateFn === updateFn && this.canEnrichActor();
  }

  isReady(): boolean {
    return this.isInitialized && this.updateFn !== null;
  }

  /**
   * Update user custom attributes in Intercom
   */
  updateUserAttributes(attributes: IntercomCustomAttributes): void {
    if (!this.isReady() || !this.updateFn) {
      return;
    }

    try {
      this.updateFn({ customAttributes: attributes });
    } catch (error) {
      console.warn("IntercomService: Attribute update unavailable");
    }
  }

  /**
   * Track an event in Intercom
   */
  trackEvent(eventName: IntercomEventName, metadata?: Record<string, unknown>): void {
    if (!this.isReady() || !this.trackEventFn) {
      return;
    }

    try {
      this.trackEventFn(eventName, metadata);
    } catch (error) {
      console.warn("IntercomService: Event tracking unavailable");
    }
  }

  /**
   * Convert a question text to its corresponding Intercom attribute name
   */
  getAttributeNameForQuestion(questionText: string): string | null {
    const normalizedQuestion = questionText
      .toLowerCase()
      .trim()
      .replace(/[?.,!']/g, "");

    // Check for exact match first
    if (QUESTION_TO_ATTRIBUTE_MAP[normalizedQuestion]) {
      return QUESTION_TO_ATTRIBUTE_MAP[normalizedQuestion];
    }

    // Check for partial match
    for (const [key, value] of Object.entries(QUESTION_TO_ATTRIBUTE_MAP)) {
      if (normalizedQuestion.includes(key) || key.includes(normalizedQuestion)) {
        return value;
      }
    }

    // Fallback: create a snake_case attribute from the question
    return `question_${normalizedQuestion.slice(0, 30).replace(/\s+/g, "_")}`;
  }

  /**
   * Sync a single questionnaire response to Intercom
   */
  syncQuestionnaireAnswer(question: FormQuestion, answer: string): void {
    if (!this.canEnrichActor()) return;
    const attributeName = this.getAttributeNameForQuestion(question.question);
    if (!attributeName) return;

    const attributes: IntercomCustomAttributes = {
      [attributeName]: answer,
    };

    this.updateUserAttributes(attributes);

    // Also track the event
    this.trackEvent(IntercomEvents.QUESTIONNAIRE_ANSWER_SUBMITTED, {
      questionId: question.id,
      questionText: question.question,
      category: question.category,
      subcategory: question.subcategory,
    });
  }

  /**
   * Sync all questionnaire responses to Intercom
   * Called on login to ensure Intercom has latest data
   */
  syncAllQuestionnaireResponses(questions: FormQuestion[], responses: FormResponse[]): void {
    if (!this.canEnrichActor()) return;
    const attributes: IntercomCustomAttributes = {};

    // Build a map of questionId -> question for quick lookup
    const questionMap = new Map<number, FormQuestion>();
    questions.forEach(q => questionMap.set(q.id, q));

    // Process each response
    responses.forEach(response => {
      const question = questionMap.get(response.questionId);
      if (!question) return;

      const attributeName = this.getAttributeNameForQuestion(question.question);
      if (attributeName) {
        attributes[attributeName] = response.answer;
      }
    });

    // Calculate questionnaire progress
    const totalQuestions = questions.length;
    const answeredQuestions = responses.length;
    const progressPercent = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

    attributes.questionnaire_progress_percent = progressPercent;
    attributes.questionnaire_completed = answeredQuestions >= totalQuestions && totalQuestions > 0;

    if (attributes.questionnaire_completed) {
      // Find the most recent response date
      const latestResponse = responses.reduce((latest, r) => {
        const responseDate = new Date(r.updatedDateUtc || r.createdDateUtc);
        return responseDate > new Date(latest.updatedDateUtc || latest.createdDateUtc) ? r : latest;
      }, responses[0]);

      if (latestResponse) {
        attributes.questionnaire_completion_date = latestResponse.updatedDateUtc || latestResponse.createdDateUtc;
      }
    }

    this.updateUserAttributes(attributes);
  }

  /**
   * Track questionnaire started event
   */
  trackQuestionnaireStarted(metadata: QuestionnaireEventMetadata): void {
    if (!this.canEnrichActor()) return;
    this.trackEvent(IntercomEvents.QUESTIONNAIRE_STARTED, metadata);
  }

  /**
   * Track questionnaire completed event and update attributes
   */
  trackQuestionnaireCompleted(metadata: QuestionnaireEventMetadata): void {
    if (!this.canEnrichActor()) return;
    this.trackEvent(IntercomEvents.QUESTIONNAIRE_COMPLETED, metadata);

    this.updateUserAttributes({
      questionnaire_completed: true,
      questionnaire_completion_date: new Date().toISOString(),
    });
  }

  /**
   * Track portfolio view event
   */
  trackPortfolioView(metadata: PortfolioViewEventMetadata): void {
    this.trackEvent(IntercomEvents.PORTFOLIO_VIEWED, metadata);

    // Update last activity
    this.updateUserAttributes({
      last_page_viewed: `portfolio_${metadata.portfolioId}`,
      last_active_date: new Date().toISOString(),
    });
  }

  /**
   * Track basket view event
   */
  trackBasketView(metadata: BasketViewEventMetadata): void {
    this.trackEvent(IntercomEvents.BASKET_VIEWED, metadata);

    // Update last activity
    this.updateUserAttributes({
      last_page_viewed: `basket_${metadata.basketId}`,
      last_active_date: new Date().toISOString(),
    });
  }

  /**
   * Track discovery page view
   */
  trackDiscoveryPageView(metadata: DiscoveryEventMetadata): void {
    this.trackEvent(IntercomEvents.DISCOVERY_PAGE_VIEWED, metadata);

    this.updateUserAttributes({
      last_page_viewed: "discovery",
      last_active_date: new Date().toISOString(),
    });
  }

  /**
   * Track login success and increment session count
   */
  trackLoginSuccess(sessionCount?: number): void {
    this.trackEvent(IntercomEvents.LOGIN_SUCCESS, {
      method: "otp",
      timestamp: new Date().toISOString(),
    });

    const attributes: IntercomCustomAttributes = {
      last_active_date: new Date().toISOString(),
    };

    if (sessionCount !== undefined) {
      attributes.total_sessions = sessionCount;
    }

    this.updateUserAttributes(attributes);
  }

  /**
   * Update engagement metrics
   */
  updateEngagementMetrics(metrics: {
    portfoliosViewedCount?: number;
    basketsViewedCount?: number;
    lastPageViewed?: string;
  }): void {
    const attributes: IntercomCustomAttributes = {
      last_active_date: new Date().toISOString(),
    };

    if (metrics.portfoliosViewedCount !== undefined) {
      attributes.portfolios_viewed_count = metrics.portfoliosViewedCount;
    }
    if (metrics.basketsViewedCount !== undefined) {
      attributes.baskets_viewed_count = metrics.basketsViewedCount;
    }
    if (metrics.lastPageViewed) {
      attributes.last_page_viewed = metrics.lastPageViewed;
    }

    this.updateUserAttributes(attributes);
  }

  /**
   * Reset the service (called on logout)
   */
  reset(): void {
    this.isInitialized = false;
    this.updateFn = null;
    this.trackEventFn = null;
  }
}

// Export singleton instance
export const intercomService = new IntercomService();
export default intercomService;
